<template>
  <section class="amp" aria-label="Model">
    <div class="amp-head">
      <div class="amp-titles">
        <b>Model</b>
        <span
          >Which model answers you here. Only your own conversation
          changes.</span
        >
      </div>
      <button type="button" class="amp-close" @click="$emit('close')">
        Close
      </button>
    </div>
    <p class="amp-warn" v-if="problem">
      {{ problem.name }} did not answer at {{ problemTime }}, so Gemini answered
      instead: {{ problem.error }}
    </p>
    <p
      class="amp-empty"
      v-if="assistant.modelsLoading && !assistant.models.length"
    >
      Loading…
    </p>
    <div class="amp-list" v-else role="radiogroup" aria-label="Model">
      <button
        v-for="m in assistant.models"
        :key="m.id"
        type="button"
        class="amp-row"
        role="radio"
        :class="{ 'is-on': m.id === assistant.modelPick }"
        :aria-checked="m.id === assistant.modelPick ? 'true' : 'false'"
        :disabled="busy"
        @click="pick(m)"
      >
        <span class="amp-dot" aria-hidden="true"></span>
        <span class="amp-text">
          <span class="amp-name"
            >{{ m.name
            }}<span class="amp-tag" v-if="m.id === assistant.modelDefault"
              >default</span
            ></span
          >
          <span class="amp-note">{{ m.note }}</span>
        </span>
      </button>
    </div>
  </section>
</template>

<script>
import { mapState } from "vuex";
import { dateLocale } from "@/i18n";

/**
 * /model (2026-10-20): the person picks which model answers them in Connect. Gemini is the default; DeepSeek shows
 * while the server has its key (Grok is out of Connect since 2026-10-28). If the pick fails, Gemini answers that
 * question and the reason shows here.
 */
export default {
  name: "AssistantModelPicker",
  emits: ["close", "picked"],
  data() {
    return { busy: false };
  },
  computed: {
    ...mapState({ assistant: (s) => s.assistant }),
    /** The failure of the provider I picked, while it stands (the server keeps one per provider). */
    problem() {
      const m = this.assistant.models.find(
        (x) => x.id === this.assistant.modelPick,
      );
      return (m && this.assistant.modelProblems[m.provider]) || null;
    },
    problemTime() {
      const d = new Date(this.problem ? this.problem.at : "");
      return isNaN(d)
        ? ""
        : d.toLocaleString(dateLocale("en-CA"), {
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          });
    },
  },
  mounted() {
    this.$store.dispatch("assistantModels");
  },
  methods: {
    async pick(m) {
      if (this.busy || m.id === this.assistant.modelPick) return;
      this.busy = true;
      const ok = await this.$store.dispatch("assistantSetModel", m.id);
      this.busy = false;
      if (ok) this.$emit("picked", m);
    },
  },
};
</script>

<style scoped>
.amp {
  box-sizing: border-box;
  flex: 0 1 auto;
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 60%;
  overflow-y: auto;
  margin: var(--s-2) 0;
  padding: 12px;
  border: 1px solid var(--border-primary);
  border-radius: 14px;
  background: var(--bg-secondary);
}
.amp-head {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}
.amp-titles {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.amp-titles b {
  font-size: var(--t-body-sm);
  font-weight: 650;
  color: var(--text-primary);
}
.amp-titles span {
  font-size: var(--t-caption);
  line-height: var(--t-caption-lh);
  color: var(--text-muted);
}
.amp-close {
  flex: none;
  padding: 0;
  border: 0;
  background: transparent;
  font: inherit;
  font-size: var(--t-caption);
  font-weight: 600;
  color: var(--text-link);
  cursor: pointer;
}
.amp-close:hover {
  text-decoration: underline;
}
.amp-warn {
  margin: 0;
  padding: 8px 10px;
  border-radius: 10px;
  background: var(--warning-bg);
  color: var(--text-primary);
  font-size: var(--t-caption);
  line-height: var(--t-caption-lh);
  overflow-wrap: anywhere;
}
.amp-empty {
  margin: 0;
  font-size: var(--t-body-sm);
  color: var(--text-muted);
}
.amp-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.amp-row {
  box-sizing: border-box;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  width: 100%;
  padding: 9px 12px;
  border: 1px solid var(--border-primary);
  border-radius: 12px;
  background: var(--bg-primary);
  color: var(--text-primary);
  font: inherit;
  text-align: left;
  cursor: pointer;
}
.amp-row:hover:not(:disabled) {
  border-color: var(--brand-orange);
}
.amp-row:disabled {
  cursor: default;
  opacity: 0.7;
}
.amp-row.is-on {
  border-color: var(--accent-primary);
  background: var(--accent-primary-bg);
}
.amp-dot {
  flex: none;
  width: 14px;
  height: 14px;
  margin-top: 2px;
  border: 2px solid var(--border-secondary);
  border-radius: 50%;
  box-sizing: border-box;
}
.amp-row.is-on .amp-dot {
  border: 4px solid var(--brand-orange);
}
.amp-text {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
}
.amp-name {
  font-size: var(--t-body-sm);
  font-weight: 600;
  line-height: 1.4;
}
.amp-tag {
  margin-left: 6px;
  padding: 0 6px;
  border-radius: 999px;
  background: var(--bg-tertiary);
  color: var(--text-muted);
  font-size: var(--t-caption);
  font-weight: 600;
}
.amp-note {
  font-size: var(--t-caption);
  line-height: var(--t-caption-lh);
  color: var(--text-muted);
}
</style>
